"use client";

import { SiteTemplatePageSectionTypeHealthDefaultListItemSettingModel } from "@/Apis/ClinicApi";
import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { HealthSectionProps } from "@/Templates/SectionsProps";
import { FC, ReactElement, ReactNode } from "react";
import HomeNineHealthSectionGenerated, {
  HomeNineHealthSectionGeneratedProps,
} from "../Sections/_generated/HomeNineHealthSection.generated";
import HomeThirteenHealthSectionGenerated, {
  HomeThirteenHealthSectionGeneratedProps,
} from "../Sections/_generated/HomeThirteenHealthSection.generated";
import HomeThreeHealthSectionGenerated, {
  HomeThreeHealthSectionGeneratedProps,
} from "../Sections/_generated/HomeThreeHealthSection.generated";
import HomeTwoHealthSectionGenerated, {
  HomeTwoHealthSectionGeneratedProps,
} from "../Sections/_generated/HomeTwoHealthSection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type GeneratedHealthRenderer = (props: HealthSectionProps) => ReactElement;

const HealthAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateHealthAppearance;

function renderHtmlValue(value?: string | null): ReactNode {
  if (!value) {
    return undefined;
  }

  return <Html value={value} />;
}

function getListItems(
  props: HealthSectionProps,
): SiteTemplatePageSectionTypeHealthDefaultListItemSettingModel[] {
  return props.lists.Default ?? [];
}

function getSectionImageUrl(props: HealthSectionProps): string | undefined {
  return props.setting.Image?.Url ?? undefined;
}

function getSectionImageAlt(
  props: HealthSectionProps,
  fallback: string,
): string {
  return props.setting.Image?.AltText ?? fallback;
}

function isImageLikeValue(value?: string | null): boolean {
  if (!value) {
    return false;
  }

  return (
    value.startsWith("/") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  );
}

function getItemImageUrl(
  item?: SiteTemplatePageSectionTypeHealthDefaultListItemSettingModel | null,
): string | undefined {
  return item?.Image?.Url ?? undefined;
}

function getItemImageAlt(
  item:
    SiteTemplatePageSectionTypeHealthDefaultListItemSettingModel | undefined,
  fallback: string,
): string {
  return item?.Image?.AltText ?? item?.Title ?? fallback;
}

function getItemVisual(
  item:
    SiteTemplatePageSectionTypeHealthDefaultListItemSettingModel | undefined,
  fallback?: string,
): string | undefined {
  if (isImageLikeValue(item?.Icon)) {
    return item?.Icon ?? undefined;
  }

  return getItemImageUrl(item) ?? fallback;
}

function getDisplayTextFromLink(link?: string | null): string | undefined {
  if (!link) {
    return undefined;
  }

  if (link.startsWith("tel:") || link.startsWith("mailto:")) {
    return link.slice(link.indexOf(":") + 1);
  }

  return link;
}

const generatedHealthRenderers: Record<string, GeneratedHealthRenderer> = {
  [HealthAppearance.Home2.key]: (props) => {
    const items = getListItems(props);

    return (
      <HomeTwoHealthSectionGenerated
        {...buildGeneratedSectionProps<HomeTwoHealthSectionGeneratedProps>(
          "HomeTwoHealthSection",
          {
            sectionDiv0Div0Div0Div0H20H2Text: props.setting.Title ?? undefined,
            sectionDiv0Div0Div0Div0H20H2Text2:
              props.setting.Description ?? props.setting.Subtitle ?? undefined,
            sectionDiv0Div0Div0Div0H20Span0ImageWithBasePath0ImageSrc:
              getSectionImageUrl(props) ?? undefined,
            sectionDiv0Div0Div0Div0H20Span0ImageWithBasePath0ImageAlt:
              getSectionImageAlt(props, "health"),
            sectionDiv0Div0Div0Div0H20Span2ImageWithBasePath0ImageSrc:
              getItemImageUrl(items[0]) ?? undefined,
            sectionDiv0Div0Div0Div0H20Span2ImageWithBasePath0ImageAlt:
              getItemImageAlt(items[0], "health"),
            trailImages: items.slice(0, 3).map((item) => ({
              src:
                getItemImageUrl(item) ??
                "/Templates/Doccure/img/about/health-03.jpg",
              text: item.Title ?? "",
              alt: getItemImageAlt(item, "health"),
            })),
            avatarImages: items.slice(3, 7).map((item) => ({
              src:
                getItemImageUrl(item) ??
                "/Templates/Doccure/img/about/patient24.jpg",
              text: item.Title ?? "",
              alt: getItemImageAlt(item, "patient"),
            })),
          },
        )}
      />
    );
  },
  [HealthAppearance.Home3.key]: (props) => (
    <HomeThreeHealthSectionGenerated
      {...buildGeneratedSectionProps<HomeThreeHealthSectionGeneratedProps>(
        "HomeThreeHealthSection",
        {
          sectionDiv0Div0Div0Div0Div0H20H2Text:
            props.setting.Title ?? undefined,
          sectionDiv0Div0Div0Div0Div0H20Span0SpanText:
            props.setting.Subtitle ?? undefined,
          sectionDiv0Div0Div0Div0Div0P0PText: renderHtmlValue(
            props.setting.Description,
          ),
          sectionDiv0Div0Div0Div0Div1Input0InputPlaceholder:
            props.setting.ButtonText ?? undefined,
          sectionDiv0Div0Div0Div0Div1Link0LinkHref:
            props.setting.ButtonLink ?? undefined,
          sectionDiv0Div0Div0Div1Div0ImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionDiv0Div0Div0Div1Div0ImageWithBasePath0ImageAlt:
            getSectionImageAlt(props, "health"),
        },
      )}
    />
  ),
  [HealthAppearance.Home9.key]: (props) => (
    <HomeNineHealthSectionGenerated
      {...buildGeneratedSectionProps<HomeNineHealthSectionGeneratedProps>(
        "HomeNineHealthSection",
        {
          sectionDiv0Div0DivText: props.setting.Subtitle ?? undefined,
          sectionDiv0Div1Div0Div3H20H2Renderer: () => (
            <h2>
              {renderHtmlValue(props.setting.Description) ??
                props.setting.Title ??
                undefined}
            </h2>
          ),
          sectionDiv0Div2Div0Div1Span0SpanText:
            props.setting.ButtonText ?? undefined,
          sectionDiv0Div2Div0Div1Link0LinkHref:
            props.setting.ButtonLink ?? undefined,
          sectionDiv0Div2Div0Div1Link0LinkText:
            getDisplayTextFromLink(props.setting.ButtonLink) ??
            props.setting.ButtonLink ??
            undefined,
          sectionImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionImageWithBasePath0ImageAlt: getSectionImageAlt(
            props,
            "health",
          ),
          healthSectionContents: getListItems(props)
            .slice(0, 3)
            .map((item) => ({
              key: item.Id,
              src:
                getItemImageUrl(item) ??
                "/Templates/Doccure/img/about/health-03.jpg",
              alt: getItemImageAlt(item, "health"),
            })),
        },
      )}
    />
  ),
  [HealthAppearance.Home13.key]: (props) => (
    <HomeThirteenHealthSectionGenerated
      {...buildGeneratedSectionProps<HomeThirteenHealthSectionGeneratedProps>(
        "HomeThirteenHealthSection",
        {
          sectionDiv0Div0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0Div0Link0LinkHref: props.setting.ButtonLink ?? undefined,
          sectionDiv0Div0Link0LinkText: props.setting.ButtonText ?? undefined,
          healthSection2346s: getListItems(props).map((item, index) => ({
            key: item.Id,
            className:
              [
                "health-item-thirteen wow fadeInUp",
                "health-item-thirteen two wow fadeInUp",
                "health-item-thirteen three wow fadeInUp",
                "health-item-thirteen four wow fadeInUp",
                "health-item-thirteen five wow fadeInUp",
                "health-item-thirteen six wow fadeInUp",
              ][index] ?? "health-item-thirteen wow fadeInUp",
            dataWowDuration: `${index + 1}s`,
            src:
              getItemVisual(
                item,
                "/Templates/Doccure/img/icons/health-icon-1.svg",
              ) ?? "/Templates/Doccure/img/icons/health-icon-1.svg",
            link0Text: item.Title ?? "",
            href: item.Link ?? "#",
            alt: getItemImageAlt(item, "health"),
          })),
        },
      )}
    />
  ),
};

const GeneratedHealthSection: FC<HealthSectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedHealthRenderers,
    HealthAppearance.Home2.key,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedHealthSection;
